import { Stack, TextField, Typography } from "@mui/material";
import { FirebaseError } from "firebase/app";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "src/contexts/AppContext";
import { useAuth } from "src/contexts/AuthContext";
import { PERMIT_TEST_BOOKING_PATH } from "src/features/dmv-booking/constants";
import requestLoginCode, {
  LoginCodeChannel,
} from "src/features/users/services/requestLoginCode";
import loginCodeErrorMessage from "src/features/users/utils/loginCodeErrorMessage";
import { trackEvent } from "src/hooks/useInstrumentation";
import useErrorHandler from "src/hooks/utility/useErrorHandler";
import Button from "src/shared/components/Button";
import SubmitButton from "src/shared/components/Button/SubmitButton";
import formatNumberToE164 from "src/shared/utils/formatting/formatNumberToE164";
import {
  DEV_ADMIN_LOGIN,
  DEV_DMV_APPOINTMENT_LOGIN,
  DEV_PERMIT_BOOKING_LOGIN,
  DEV_STUDENT_LOGIN,
} from "../../../shared-utils/devLoginAccounts";
import DevelopmentOnlyLoginPreview from "./DevelopmentOnlyLoginPreview";
import LoginLinkAction from "./LoginLinkAction";
import LoginStepLayout from "./LoginStepLayout";
import {
  loginFieldSx,
  loginPrimaryButtonSx,
  loginSecondaryButtonSx,
} from "./loginStyles";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Mirrors the server's identifier rules (`resolveLoginIdentifier`) so obvious
 * typos get caught before a round trip: anything with "@" must look like an
 * email, anything else must parse as a phone number.
 */
function isPlausibleIdentifier(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed) return false;
  if (trimmed.includes("@")) return EMAIL_REGEX.test(trimmed.toLowerCase());
  return formatNumberToE164(trimmed) !== null;
}

interface Props {
  identifier: string;
  onIdentifierChange: (identifier: string) => void;
  onCodeSent: (channel: LoginCodeChannel) => void;
  onUsePassword: () => void;
}

export default function IdentifierStep({
  identifier,
  onIdentifierChange,
  onCodeSent,
  onUsePassword,
}: Props): JSX.Element {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { clients } = useAppContext();
  const authCtx = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();

  // Dev-only auto-login: signs in against the local Auth emulator with the
  // seeded credentials, sidestepping the code-request callable that isn't
  // running locally. Only reachable via DevelopmentOnlyLoginPreview, which
  // renders solely in development.
  const loginWithDevCredentials = async (
    email: string,
    password: string,
    destination = "/"
  ) => {
    if (process.env.NODE_ENV !== "development") {
      throw new Error("Dev auto-login is only available in development.");
    }
    try {
      await authCtx?.login(email, password);
      navigate(destination, { replace: true });
    } catch (e) {
      handleError(e);
    }
  };

  const handleContinue = async () => {
    setError(null);
    if (!isPlausibleIdentifier(identifier)) {
      setError(
        t(
          "That doesn't look like an email or phone number. Check it and try again."
        )
      );
      return;
    }

    try {
      setLoading(true);
      const { channel } = await requestLoginCode(
        { identifier: identifier.trim() },
        clients.functions
      );
      trackEvent("Login Screen - Code Requested", { channel });
      onCodeSent(channel);
    } catch (e) {
      if (e instanceof FirebaseError) {
        setError(loginCodeErrorMessage(e, t, "request"));
      } else handleError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginStepLayout
      title={t("Welcome back")}
      description={t(
        "Enter the email address or phone number you used to join Emerge Career."
      )}
    >
      <Stack
        component="form"
        spacing={{ xs: 3, sm: 3.5 }}
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
      >
        <Stack spacing={1.25}>
          <TextField
            autoFocus
            fullWidth
            type="text"
            value={identifier}
            placeholder={t("Email or phone number")}
            error={!!error}
            helperText={error}
            inputProps={{
              "aria-label": t("Email or phone number"),
              autoComplete: "username",
              inputMode: "text",
            }}
            sx={loginFieldSx}
            onChange={(e) => {
              setError(null);
              onIdentifierChange(e.target.value);
            }}
          />
          {!error && (
            <Typography color="text.secondary" variant="body2">
              {t("We'll send a 6-digit sign-in code by email or text.")}
            </Typography>
          )}
        </Stack>

        <Stack spacing={1.5}>
          <SubmitButton
            fullWidth
            disabled={!identifier.trim().length}
            loading={loading}
            sx={loginPrimaryButtonSx}
          >
            {t("Continue")}
          </SubmitButton>
          <Button
            fullWidth
            variant="outlined"
            onClick={onUsePassword}
            size="large"
            sx={loginSecondaryButtonSx}
          >
            {t("Log in with Password")}
          </Button>
        </Stack>

        <Stack spacing={0.25} alignItems="center">
          <LoginLinkAction onClick={() => navigate("/register")}>
            {t("Register for a new account")}
          </LoginLinkAction>
          <DevelopmentOnlyLoginPreview
            onLoginAsStudent={() =>
              loginWithDevCredentials(
                DEV_STUDENT_LOGIN.email,
                DEV_STUDENT_LOGIN.password
              )
            }
            onLoginAsAdmin={() =>
              loginWithDevCredentials(
                DEV_ADMIN_LOGIN.email,
                DEV_ADMIN_LOGIN.password
              )
            }
            onTestPermitBooking={() =>
              loginWithDevCredentials(
                DEV_PERMIT_BOOKING_LOGIN.email,
                DEV_PERMIT_BOOKING_LOGIN.password,
                PERMIT_TEST_BOOKING_PATH
              )
            }
            onTestDmvAppointment={() =>
              loginWithDevCredentials(
                DEV_DMV_APPOINTMENT_LOGIN.email,
                DEV_DMV_APPOINTMENT_LOGIN.password,
                "/practice-hub"
              )
            }
            onPreviewEmailCode={() => {
              onIdentifierChange("test@example.com");
              onCodeSent("email");
            }}
            onPreviewSmsCode={() => {
              onIdentifierChange("(617) 555-0123");
              onCodeSent("sms");
            }}
            onPreviewPassword={() => {
              if (!identifier.includes("@")) {
                onIdentifierChange("test@example.com");
              }
              onUsePassword();
            }}
          />
        </Stack>
      </Stack>
    </LoginStepLayout>
  );
}
